<script setup lang="ts">
import { RouterView, useRoute } from 'vue-router'
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
// @ts-ignore: 自定义模块
import { getHospitalType } from './common/auth'

// 判断是否为移动端
const isMobile = ref(false)

// 获取医院类型并生成动态标题
const hospitalType = ref(getHospitalType())
const isCommunity = computed(() => hospitalType.value === '2')

// 动态平台标题
const platformTitle = computed(() => {
  return isCommunity.value ? '社区护工管理平台' : 'AIcare·爱护无忧'
})

// 检查设备是否为移动端
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

// 获取当前路由
const route = useRoute()

// 判断是否显示动画效果（只在登录页面和注册页面显示）
const showAnimations = computed(() => {
  const path = route.path
  return path === '/login' || path === '/hospital-register'
})

// 监听窗口大小变化
onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})
</script>
<template>
  <div class="app-container">
    <!-- 背景动画效果 - 只在登录和注册页面显示 -->
    <div v-if="showAnimations" class="background-animation">
      <div class="wave wave1"></div>
      <div class="wave wave2"></div>
      <div class="wave wave3"></div>
      <div class="wave wave4"></div>
    </div>
    <!-- 浮动粒子效果 - 只在登录和注册页面显示 -->
    <div v-if="showAnimations" class="particles-container">
      <div v-for="i in 150" :key="i" class="particle" :style="{
        '--size': `${Math.random() * 12 + 3}px`,
        '--left': `${Math.random() * 100}%`,
        '--delay': `${Math.random() * 10}s`,
        '--duration': `${Math.random() * 15 + 10}s`,
        '--color': `var(--particle-color-${Math.floor(Math.random() * 5) + 1})`,
        '--opacity': `${Math.random() * 0.5 + 0.5}`,
      }"></div>
    </div>
    <!-- 应用标题 - 根据设备类型调整位置，只在有动画时显示 -->
    <div v-if="showAnimations" class="app-title" :class="{ 'app-title-mobile': isMobile }">
      <h1 v-if="!isMobile">{{ platformTitle }}</h1>
      <p v-if="!isMobile">医院管理平台</p>
    </div>
    <!-- 主要内容 -->
    <router-view />
  </div>
</template>
<style>
/* 全局样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'PingFang SC', 'Helvetica Neue', Helvetica, 'Microsoft YaHei', Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #333;
  background-color: #f5f7fa;
  overflow-x: hidden;
  width: 100%;
  touch-action: manipulation;
  /* 防止iOS双击缩放 */
  /* 定义粒子颜色变量 */
  --particle-color-1: rgba(59, 130, 246, 0.3);
  /* 蓝色 */
  --particle-color-2: rgba(239, 68, 68, 0.3);
  /* 红色 */
  --particle-color-3: rgba(16, 185, 129, 0.3);
  /* 绿色 */
  --particle-color-4: rgba(245, 158, 11, 0.3);
  /* 橙色 */
  --particle-color-5: rgba(139, 92, 246, 0.3);
  /* 紫色 */
}

/* 添加移动端输入框焦点样式 */
input:focus {
  font-size: 16px !important;
  touch-action: manipulation;
}

.app-container {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  width: 100%;
}

/* 背景动画效果 */
.background-animation {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -10;
  overflow: hidden;
}

.wave {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 200%;
  height: 100%;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320"><path fill="%233b82f6" fill-opacity="0.05" d="M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path></svg>');
  background-repeat: repeat-x;
  background-position: 0 bottom;
  background-size: 50% 100px;
  transform: translateX(0);
}

.wave1 {
  opacity: 0.3;
  animation: wave 15s linear infinite;
  z-index: -9;
}

.wave2 {
  opacity: 0.2;
  animation: wave 25s linear infinite reverse;
  z-index: -8;
}

.wave3 {
  opacity: 0.1;
  animation: wave 35s linear infinite;
  z-index: -7;
}

.wave4 {
  opacity: 0.05;
  animation: wave 45s linear infinite reverse;
  z-index: -6;
}

@keyframes wave {
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(-50%);
  }
}

/* 浮动粒子效果 */
.particles-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -5;
  overflow: hidden;
}

.particle {
  position: absolute;
  width: var(--size);
  height: var(--size);
  background-color: var(--color);
  border-radius: 50%;
  left: var(--left);
  bottom: -100px;
  animation: float var(--duration) cubic-bezier(0.25, 0.1, 0.25, 1) infinite;
  animation-delay: var(--delay);
  filter: blur(1px);
  opacity: var(--opacity);
  will-change: transform, opacity;
}

@keyframes float {
  0% {
    transform: translateY(0) rotate(0deg) scale(0.8);
    opacity: 0;
  }

  5% {
    opacity: var(--opacity);
  }

  25% {
    transform: translateY(-25vh) rotate(90deg) scale(1);
  }

  50% {
    transform: translateY(-50vh) rotate(180deg) scale(1.1);
    opacity: var(--opacity);
  }

  75% {
    transform: translateY(-75vh) rotate(270deg) scale(1);
  }

  95% {
    opacity: var(--opacity);
  }

  100% {
    transform: translateY(-100vh) rotate(360deg) scale(0.8);
    opacity: 0;
  }
}

/* 应用标题 */
.app-title {
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 1000;
  color: #3b82f6;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 1;
  transition: all 0.3s ease;
}

/* 移动端右侧标题样式 */
.app-title-mobile {
  left: auto;
  right: 20px;
  text-align: right;
}

.app-title h1 {
  font-size: 24px;
  font-weight: bold;
  margin: 0;
}

.app-title p {
  font-size: 14px;
  opacity: 0.8;
}

/* 响应式调整 */
@media (max-width: 768px) {

  .app-title,
  .app-title-mobile {
    top: 10px;
  }

  .app-title {
    left: 10px;
  }

  .app-title-mobile {
    right: 10px;
  }

  .app-title h1 {
    font-size: 20px;
  }

  .app-title p {
    font-size: 12px;
  }
}
</style>
